import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, LogOut, FileDown, ArrowLeft, Inbox, Send, Mail } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api, { API } from "@/lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { TestimonialsManager, PartnersManager, ImagesManager } from "@/components/admin/ContentManagers";
import { InsightsManager } from "@/components/admin/InsightsManager";

const TOKEN_KEY = "norex_admin_token";

const STATUS_STYLES = {
  new: "text-gold border-gold/50",
  contacted: "text-sky-300 border-sky-300/40",
  quoted: "text-amber-200 border-amber-200/40",
  closed: "text-emerald-300 border-emerald-300/40",
};

const ReplyBox = ({ rfq, token }) => {
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const [replies, setReplies] = useState(rfq.replies || []);

  const send = async () => {
    if (!msg.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post(
        `/admin/rfqs/${rfq.id}/reply`,
        { message: msg },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setReplies([...replies, data.reply]);
      setMsg("");
      toast.success(`Reply emailed to ${rfq.email}`);
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Failed to send reply");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-4 border-t border-[#1E293B] pt-4" data-testid={`admin-rfq-reply-${rfq.id}`}>
      {replies.length > 0 && (
        <div className="space-y-2 mb-3">
          {replies.map((rep, i) => (
            <p key={i} className="text-xs text-[#94A3B8]">
              <span className="text-gold">Replied {new Date(rep.sent_at).toLocaleString()}:</span> {rep.message}
            </p>
          ))}
        </div>
      )}
      <div className="flex gap-2">
        <Input
          value={msg}
          onChange={(e) => setMsg(e.target.value)}
          data-testid={`admin-rfq-reply-input-${rfq.id}`}
          placeholder={`Reply to ${rfq.full_name} (sent by email)...`}
          className="bg-midnight border-[#1E293B] text-[#F4F6F8] h-10 rounded-sm text-sm focus-visible:ring-gold/60"
        />
        <button
          onClick={send}
          disabled={busy}
          data-testid={`admin-rfq-reply-send-${rfq.id}`}
          className="inline-flex items-center gap-2 bg-gold text-midnight text-xs font-semibold px-4 rounded-full hover:bg-gold-light transition-colors duration-200 disabled:opacity-60 shrink-0"
        >
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />} Send
        </button>
      </div>
    </div>
  );
};

const Field = ({ label, value }) => (
  <div>
    <p className="text-[10px] uppercase tracking-[0.2em] text-[#94A3B8]">{label}</p>
    <p className="text-sm text-[#F4F6F8] break-words">{value || "—"}</p>
  </div>
);

const Login = ({ onLogin }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      localStorage.setItem(TOKEN_KEY, data.access_token);
      onLogin(data.access_token);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Login failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6">
      <form
        data-testid="admin-login-form"
        onSubmit={submit}
        className="w-full max-w-md bg-surface border border-[#1E293B] p-10"
      >
        <div className="flex items-center gap-3 mb-8">
          <span className="w-9 h-9 bg-gold flex items-center justify-center font-display font-black text-midnight text-lg">N</span>
          <div>
            <p className="font-display font-bold text-[#F4F6F8]">NOREX Admin</p>
            <p className="text-xs text-[#94A3B8]">Submissions dashboard</p>
          </div>
        </div>
        <div className="space-y-5">
          <div>
            <Label htmlFor="admin-email" className="text-xs uppercase tracking-wider text-[#94A3B8]">Email</Label>
            <Input id="admin-email" data-testid="admin-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
              className="bg-midnight border-[#1E293B] text-[#F4F6F8] h-12 rounded-sm focus-visible:ring-gold/60" placeholder="admin@norextrading.com" />
          </div>
          <div>
            <Label htmlFor="admin-password" className="text-xs uppercase tracking-wider text-[#94A3B8]">Password</Label>
            <Input id="admin-password" data-testid="admin-password-input" type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
              className="bg-midnight border-[#1E293B] text-[#F4F6F8] h-12 rounded-sm focus-visible:ring-gold/60" placeholder="••••••••" />
          </div>
          {error && <p data-testid="admin-login-error" className="text-sm text-red-400">{error}</p>}
          <button
            data-testid="admin-login-button"
            type="submit"
            disabled={loading}
            className="w-full inline-flex items-center justify-center gap-2 bg-gold text-midnight font-semibold px-8 py-3.5 rounded-full hover:bg-gold-light transition-colors duration-200 disabled:opacity-60"
          >
            {loading && <Loader2 className="w-4 h-4 animate-spin" />}
            {loading ? "Signing in..." : "Sign In"}
          </button>
          <Link to="/" data-testid="admin-back-home-link" className="flex items-center justify-center gap-2 text-sm text-[#94A3B8] hover:text-gold transition-colors duration-200">
            <ArrowLeft className="w-4 h-4" /> Back to website
          </Link>
        </div>
      </form>
    </div>
  );
};

const Dashboard = ({ token, onLogout }) => {
  const [tab, setTab] = useState("rfqs");
  const [rfqs, setRfqs] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const headers = { Authorization: `Bearer ${token}` };
        const [r, s] = await Promise.all([
          api.get("/admin/rfqs", { headers }),
          api.get("/admin/suppliers", { headers }),
        ]);
        setRfqs(r.data);
        setSuppliers(s.data);
      } catch {
        toast.error("Session expired or failed to load. Please sign in again.");
        onLogout();
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [token, onLogout]);

  const items = tab === "rfqs" ? rfqs : suppliers;

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/admin/rfqs/${id}/status`, { status }, { headers: { Authorization: `Bearer ${token}` } });
      setRfqs(rfqs.map((r) => (r.id === id ? { ...r, status } : r)));
      toast.success(`Marked as ${status}`);
    } catch {
      toast.error("Failed to update status");
    }
  };

  const sendDigest = async () => {
    try {
      const { data } = await api.post("/admin/digest", {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Digest emailed — ${data.rfqs} RFQs, ${data.suppliers} suppliers this week`);
    } catch {
      toast.error("Failed to send digest");
    }
  };

  return (
    <div className="min-h-screen max-w-7xl mx-auto px-6 lg:px-10 py-10">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-10">
        <div className="flex items-center gap-3">
          <span className="w-9 h-9 bg-gold flex items-center justify-center font-display font-black text-midnight text-lg">N</span>
          <div>
            <h1 className="font-display font-bold text-xl text-[#F4F6F8]" data-testid="admin-dashboard-title">Submissions Dashboard</h1>
            <p className="text-xs text-[#94A3B8]">RFQs and supplier applications</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Link to="/" data-testid="admin-back-site-link" className="text-sm text-[#94A3B8] hover:text-gold transition-colors duration-200">
            View website
          </Link>
          <button
            data-testid="admin-logout-button"
            onClick={onLogout}
            className="inline-flex items-center gap-2 border border-white/20 text-[#F4F6F8] text-sm px-4 py-2 rounded-full hover:border-gold hover:text-gold transition-colors duration-200"
          >
            <LogOut className="w-4 h-4" /> Logout
          </button>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mb-8">
        {[
          ["rfqs", `RFQs (${rfqs.length})`],
          ["suppliers", `Suppliers (${suppliers.length})`],
          ["testimonials", "Testimonials"],
          ["partners", "Partners"],
          ["images", "Site Photos"],
          ["insights", "Insights"],
        ].map(([key, label]) => (
          <button
            key={key}
            data-testid={`admin-tab-${key}`}
            onClick={() => setTab(key)}
            className={`px-5 py-2.5 rounded-full text-sm font-semibold transition-colors duration-200 ${
              tab === key ? "bg-gold text-midnight" : "border border-[#1E293B] text-[#94A3B8] hover:text-[#F4F6F8]"
            }`}
          >
            {label}
          </button>
        ))}
        {tab === "rfqs" && (
          <>
            <button
              data-testid="admin-send-digest"
              onClick={sendDigest}
              className="ml-auto inline-flex items-center gap-2 border border-[#1E293B] text-[#94A3B8] text-sm px-4 py-2 rounded-full hover:text-gold hover:border-gold/50 transition-colors duration-200"
            >
              <Mail className="w-4 h-4" /> Send Weekly Digest
            </button>
            <a
              data-testid="admin-export-rfqs"
              href={`${API}/admin/export/rfqs.csv?auth=${token}`}
              className="inline-flex items-center gap-2 border border-gold/50 text-gold text-sm px-4 py-2 rounded-full hover:bg-gold hover:text-midnight transition-colors duration-200"
            >
              <FileDown className="w-4 h-4" /> Export CSV
            </a>
          </>
        )}
        {tab === "suppliers" && (
          <a
            data-testid="admin-export-suppliers"
            href={`${API}/admin/export/suppliers.csv?auth=${token}`}
            className="ml-auto inline-flex items-center gap-2 border border-gold/50 text-gold text-sm px-4 py-2 rounded-full hover:bg-gold hover:text-midnight transition-colors duration-200"
          >
            <FileDown className="w-4 h-4" /> Export CSV
          </a>
        )}
      </div>

      {tab === "insights" ? (
        <InsightsManager token={token} />
      ) : tab === "testimonials" ? (
        <TestimonialsManager token={token} />
      ) : tab === "partners" ? (
        <PartnersManager token={token} />
      ) : tab === "images" ? (
        <ImagesManager token={token} />
      ) : loading ? (
        <div className="flex justify-center py-24" data-testid="admin-loading">
          <Loader2 className="w-8 h-8 text-gold animate-spin" />
        </div>
      ) : items.length === 0 ? (
        <div data-testid="admin-empty-state" className="border border-dashed border-[#1E293B] py-24 flex flex-col items-center gap-3 text-[#94A3B8]">
          <Inbox className="w-8 h-8" />
          <p className="text-sm">No {tab === "rfqs" ? "RFQs" : "supplier applications"} yet.</p>
        </div>
      ) : (
        <div className="space-y-4" data-testid={`admin-${tab}-list`}>
          {tab === "rfqs"
            ? rfqs.map((r) => (
                <div key={r.id} data-testid={`admin-rfq-${r.id}`} className="bg-surface border border-[#1E293B] p-6">
                  <div className="flex flex-wrap items-start justify-between gap-3 mb-5">
                    <div>
                      <p className="font-display font-semibold text-[#F4F6F8]">{r.product_required}</p>
                      <p className="text-xs text-[#94A3B8]">{r.company_name} · {new Date(r.created_at).toLocaleString()}</p>
                      <div className="flex items-center gap-3 mt-3">
                        <span data-testid={`admin-rfq-status-badge-${r.id}`} className={`text-[10px] uppercase tracking-[0.2em] px-2.5 py-1 rounded-full border ${STATUS_STYLES[r.status || "new"]}`}>
                          {r.status || "new"}
                        </span>
                        <Select value={r.status || "new"} onValueChange={(v) => updateStatus(r.id, v)}>
                          <SelectTrigger data-testid={`admin-rfq-status-${r.id}`} className="h-8 w-36 bg-midnight border-[#1E293B] text-[#F4F6F8] text-xs rounded-sm">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-surface2 border-[#1E293B] text-[#F4F6F8]">
                            {["new", "contacted", "quoted", "closed"].map((s) => (
                              <SelectItem key={s} value={s} data-testid={`admin-rfq-status-option-${s}`} className="text-xs capitalize">{s}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    {r.document && (
                      <a
                        data-testid={`admin-rfq-doc-${r.id}`}
                        href={`${API}/files/${r.document.storage_path}?auth=${token}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 text-xs text-gold border border-gold/40 px-3 py-1.5 rounded-full hover:bg-gold hover:text-midnight transition-colors duration-200"
                      >
                        <FileDown className="w-3.5 h-3.5" /> {r.document.original_filename}
                      </a>
                    )}
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <Field label="Contact" value={r.full_name} />
                    <Field label="Email" value={r.email} />
                    <Field label="Phone" value={r.phone} />
                    <Field label="Country" value={r.country} />
                    <Field label="Quantity" value={r.quantity} />
                    <Field label="Destination" value={r.destination} />
                    <Field label="Delivery Terms" value={r.delivery_terms} />
                    <Field label="Specifications" value={r.specifications} />
                  </div>
                  {r.additional_info && (
                    <p className="mt-4 text-sm text-[#94A3B8] border-t border-[#1E293B] pt-4">{r.additional_info}</p>
                  )}
                  <ReplyBox rfq={r} token={token} />
                </div>
              ))
            : suppliers.map((s) => (
                <div key={s.id} data-testid={`admin-supplier-${s.id}`} className="bg-surface border border-[#1E293B] p-6">
                  <div className="flex flex-wrap items-start justify-between gap-3 mb-5">
                    <div>
                      <p className="font-display font-semibold text-[#F4F6F8]">{s.company_name}</p>
                      <p className="text-xs text-[#94A3B8]">{new Date(s.created_at).toLocaleString()}</p>
                    </div>
                    <span className="text-xs text-gold border border-gold/40 px-3 py-1.5 rounded-full">{s.products}</span>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <Field label="Contact" value={s.contact_person} />
                    <Field label="Email" value={s.email} />
                    <Field label="Phone" value={s.phone} />
                    <Field label="Country" value={s.country} />
                  </div>
                  {s.message && <p className="mt-4 text-sm text-[#94A3B8] border-t border-[#1E293B] pt-4">{s.message}</p>}
                </div>
              ))}
        </div>
      )}
    </div>
  );
};

export default function AdminPage() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
  };

  return (
    <div data-testid="admin-page" className="min-h-screen bg-midnight">
      {token ? <Dashboard token={token} onLogout={logout} /> : <Login onLogin={setToken} />}
    </div>
  );
}
